import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  UserIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  MapPinIcon,
  BuildingLibraryIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Reference } from '../types/Reference';
import { referenceApi } from '../services/referenceApi';
import ReferenceForm from './ReferenceForm';
import Layout from './Layout';
import { useBranch } from '../contexts/BranchContext';
import PermissionGate from './PermissionGate';

const ReferenceList: React.FC = () => {
  const navigate = useNavigate();
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [references, setReferences] = useState<Reference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingReference, setEditingReference] = useState<Reference | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [imageUrls, setImageUrls] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    if (!branchLoading) {
      fetchReferences();
    }
  }, [currentBranch, isViewingAllBranches, branchLoading]);

  const fetchImageUrls = useCallback(async (references: Reference[]) => {
    try {
      const imagePromises = references
        .filter(ref => ref.imageUrl)
        .map(async (ref) => {
          try {
            const response = await referenceApi.getImageUrl(ref.id, 'personal');
            if (response.success && 'data' in response) {
              return { id: ref.id, url: (response.data as { signedUrl: string }).signedUrl };
            }
          } catch (error) {
            console.error(`Error fetching image for reference ${ref.id}:`, error);
          }
          return null;
        });

      const results = await Promise.all(imagePromises);
      const newImageUrls: { [key: number]: string } = {};
      
      results.forEach(result => {
        if (result) {
          newImageUrls[result.id] = result.url;
        }
      });

      setImageUrls(prev => ({ ...prev, ...newImageUrls }));
    } catch (error) {
      console.error('Error fetching image URLs:', error);
    }
  }, []);

  const fetchReferences = async (page: number = 1, search: string = '') => {
    try {
      setLoading(true);
      setError(null);
      const response = await referenceApi.getReferences(page, 20, search);
      if (response.success) {
        setReferences(response.data);
        setCurrentPage(response.pagination.currentPage);
        setTotalPages(response.pagination.totalPages);
        setTotalRecords(response.pagination.totalRecords);
        
        // Fetch image URLs for all references
        await fetchImageUrls(response.data);
      } else {
        setError('Failed to fetch references');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch references');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    fetchReferences(1, e.target.value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchReferences(page, searchTerm);
  };

  const handleEdit = async (reference: Reference) => {
    try {
      // Fetch full reference data
      const response = await referenceApi.getReferenceById(reference.id);
      if (response.success) {
        setEditingReference(response.data);
        setShowForm(true);
      } else {
        alert('Failed to load reference details');
      }
    } catch (error) {
      console.error('Error fetching reference details:', error);
      alert('Failed to load reference details');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this reference?')) {
      try {
        setDeletingId(id);
        const response = await referenceApi.deleteReference(id);
        if (response.success) {
          setReferences(prev => prev.filter(ref => ref.id !== id));
        } else {
          alert('Failed to delete reference');
        }
      } catch (error) {
        console.error('Error deleting reference:', error);
        alert('Failed to delete reference');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleView = (id: number) => {
    navigate(`/references/${id}`);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingReference(null);
    fetchReferences(currentPage, searchTerm);
  };

  const handleFormSave = (reference: Reference) => {
    if (editingReference) {
      setReferences(prev => prev.map(ref => ref.id === reference.id ? reference : ref));
    } else {
      setReferences(prev => [reference, ...prev]);
    }
    handleFormClose();
  };


  if (loading) {
    return (
      <Layout title="References" subtitle="Loading...">
        <div className="flex items-center justify-center py-12">
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading references...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <p>{error}</p>
        <button 
          onClick={() => fetchReferences()}
          className="mt-4 text-blue-600 hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <Layout title="References" subtitle="Manage reference information">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">References</h1>
              <p className="text-gray-600">Manage reference information</p>
            </div>
          <PermissionGate module="references" permission="create">
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Reference
            </button>
          </PermissionGate>
        </div>

        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search references by name, email, or phone..."
            value={searchTerm}
            onChange={handleSearch}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* References Grid */}
      {references.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No References Found' : 'No References Yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm 
              ? 'Try adjusting your search terms.' 
              : 'Get started by adding your first reference.'
            }
          </p>
          {!searchTerm && (
            <PermissionGate module="references" permission="create">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Reference
              </button>
            </PermissionGate>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {references.map((reference: Reference) => (
            <div 
              key={reference.id} 
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleView(reference.id)}
            >
              {/* Reference Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    {reference.imageUrl && imageUrls[reference.id] ? (
                      <img
                        src={imageUrls[reference.id]}
                        alt={reference.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <UserIcon className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{reference.name}</h3>
                      <p className="text-sm text-gray-500">Reference</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                    <PermissionGate module="references" permission="view">
                      <button
                        onClick={() => handleView(reference.id)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </PermissionGate>
                    <PermissionGate module="references" permission="edit">
                      <button
                        onClick={() => handleEdit(reference)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </PermissionGate>
                    <PermissionGate module="references" permission="delete">
                      <button
                        onClick={() => handleDelete(reference.id)}
                        disabled={deletingId === reference.id}
                        className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>

              {/* Reference Details */}
              <div className="p-6 space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <PhoneIcon className="w-4 h-4 mr-3 text-gray-400" />
                    <span>{reference.contactNumber}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <EnvelopeIcon className="w-4 h-4 mr-3 text-gray-400" />
                    <span className="truncate">{reference.email}</span>
                  </div>
                  <div className="flex items-start text-sm text-gray-600">
                    <MapPinIcon className="w-4 h-4 mr-3 text-gray-400 mt-0.5" />
                    <span className="line-clamp-2">{reference.address}</span>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <BuildingLibraryIcon className="w-4 h-4 mr-3 text-gray-400" />
                    <span className="font-medium">Bank Details</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>{reference.bankAccountName}</p>
                    <p className="text-xs text-gray-500">****{reference.bankAccountNumber.slice(-4)}</p>
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * 20, totalRecords)}
                </span>{' '}
                of <span className="font-medium">{totalRecords}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

        {/* Reference Form Modal */}
        {showForm && (
          <ReferenceForm
            reference={editingReference}
            onClose={handleFormClose}
            onSave={handleFormSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default ReferenceList;
